import uniqBy from "lodash/uniqBy";

import { EntityPath, Reference } from "@/types/sos-types";
import { Adjustment } from "./adjustments";
import { Build } from "./builds";
import { ItemReceipt, ReceivingCost } from "./item-receipts";
import { Item } from "./items";
import { PurchaseOrder } from "./purchase-orders";
import { Shipment } from "./shipments";
import { Transfer } from "./transfers";
import { WorkOrder } from "./work-orders";
import { BomRecordsForParents } from "./bom-records";

export interface ItemNotOnSalesOrder {
  item: Item;
  sources: (Required<Reference> & { type: SupportedEntityType })[];
}

type SupportedEntityType = Extract<
  EntityPath,
  "purchaseorder" | "itemreceipt" | "workorder" | "build" | "shipment" | "transfer" | "adjustment"
>;
interface LineItemWithItemAndJob {
  item: Item;
  job: Reference | null;
}

interface ItemAndSourceNotOnSalesOrder {
  item: Item;
  source: { type: SupportedEntityType; id: number; name: string };
}

/**
 * Gets a list of all items that are not part of the expanded BOM derived from the sales order line items, but can be
 * found on other relevant records (POs, WOs, builds, etc).
 */
export function getItemsNotOnSalesOrder(
  salesOrderBomRecords: BomRecordsForParents,
  purchaseOrders: PurchaseOrder[],
  jobItemReceipts: ItemReceipt[],
  workOrders: WorkOrder[],
  builds: Build[],
  shipments: Shipment[],
  transfers: Transfer[],
  adjustments: Adjustment[],
): ItemNotOnSalesOrder[] {
  // Get a list of all items assigned to the job that are not part of the sales order BOM
  const salesOrderItemsById = salesOrderBomRecords.itemsById;
  const itemsNotOnSalesOrder = [
    ...purchaseOrders.flatMap((po) =>
      getItemsNotOnSalesOrderForRecord("purchaseorder", po, po.jobLines, salesOrderItemsById),
    ),
    ...jobItemReceipts.flatMap((receipt) => [
      ...getItemsNotOnSalesOrderForRecord("itemreceipt", receipt, receipt.jobLines, salesOrderItemsById),
      ...getItemsNotOnSalesOrderForRecord("itemreceipt", receipt, receipt.jobReceivingCosts, salesOrderItemsById),
    ]),
    ...workOrders.flatMap((wo) => getItemsNotOnSalesOrderForRecord("workorder", wo, wo.jobLines, salesOrderItemsById)),
    ...builds.flatMap((build) => {
      const lines = [...build.inputs, ...build.outputs];
      return getItemsNotOnSalesOrderForRecord("build", build, lines, salesOrderItemsById);
    }),
    ...shipments.flatMap((shipment) =>
      getItemsNotOnSalesOrderForRecord("shipment", shipment, shipment.jobLines, salesOrderItemsById),
    ),
    ...transfers.flatMap((transfer) =>
      getItemsNotOnSalesOrderForRecord("transfer", transfer, transfer.salesOrderRackLines, salesOrderItemsById),
    ),
    ...adjustments.flatMap((adjustment) =>
      getItemsNotOnSalesOrderForRecord("adjustment", adjustment, adjustment.salesOrderRackLines, salesOrderItemsById),
    ),
  ];

  // Group the list by unique item id before returning; Also make sure to remove any duplicates from the sources (i.e.
  // if the same item appears on multiple lines in the same record)
  return Array.from(Map.groupBy(itemsNotOnSalesOrder, (x) => x.item.id).values()).map<ItemNotOnSalesOrder>((group) => ({
    item: group[0]!.item,
    sources: uniqBy(
      group.map((x) => x.source),
      (x) => x.type + x.id.toFixed(),
    ),
  }));
}

/**
 * Get all items for the provided record and line items that are assigned to the current job, but cannot be found on the
 * expanded BOM derived from the sales order line items.
 *
 * @param recordType - The type of the record (e.g. "purchaseorder", "itemreceipt", etc.)
 * @param record - The record object
 * @param jobLines - The line items from the record that are associated with the current job (i.e. relevant for the current sales order tracker context).
 * @param salesOrderItemsById - A map of items by ID that are part of the sales order BOM, used to filter out items that are already included in the sales order.
 */
const getItemsNotOnSalesOrderForRecord = (
  recordType: SupportedEntityType,
  record: { id: number; number: string },
  jobLines: (LineItemWithItemAndJob | ReceivingCost)[],
  salesOrderItemsById: Map<number, Item>,
): ItemAndSourceNotOnSalesOrder[] => {
  return jobLines
    .map((x) => x.item as Item)
    .filter((itemRef) => !salesOrderItemsById.has(itemRef.id))
    .map((itemRef) => ({
      source: { type: recordType, id: record.id, name: record.number },
      item: itemRef,
    }));
};
